import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { Link as RouterLink } from "react-router-dom";
import type { MembershipAttribution } from "../../utils/traceMembership";

interface AttributionChipProps {
  eventId: string;
  attribution: MembershipAttribution;
}

/**
 * Renders one `MembershipAttribution` as a compact chip set. Group chips
 * link to the GroupEditPage so the organizer can jump to the audience
 * composition the path resolves through.
 */
export function AttributionChip({ eventId, attribution }: AttributionChipProps) {
  if (attribution.kind === "personal") {
    return <Chip label="Direct" size="small" color="primary" variant="outlined" />;
  }
  if (attribution.kind === "role") {
    return (
      <Chip
        label={attribution.audienceName}
        size="small"
        variant="outlined"
        color="default"
      />
    );
  }
  if (attribution.kind === "group") {
    return (
      <Chip
        component={RouterLink}
        to={`/events/${eventId}/groups/${attribution.audienceId}`}
        clickable
        label={attribution.audienceName}
        size="small"
        color="secondary"
        variant="outlined"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
  // Anonymous composition.
  if (attribution.via.length === 0) {
    return (
      <Tooltip title="Targeted via an anonymous composition that no longer resolves to a recognizable path">
        <Chip label="Anonymous mix" size="small" variant="outlined" />
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      <Chip label="Mix:" size="small" variant="filled" sx={{ bgcolor: "action.hover" }} />
      {attribution.via.map((v, idx) => (
        <AttributionChip
          key={`${v.kind}-${idx}`}
          eventId={eventId}
          attribution={v}
        />
      ))}
    </Stack>
  );
}
